import React, { useState } from 'react';
import { MapPin, Plus, Settings, ChevronDown } from 'lucide-react';
import { useLocation } from '../../contexts/LocationContext';
import LocationModal from './LocationModal';

const LocationSelector = () => {
  const { locations, currentLocation, switchLocation } = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'manage'

  const handleLocationSwitch = (location) => {
    switchLocation(location);
    setIsDropdownOpen(false);
  };

  const handleAddLocation = () => {
    setModalMode('add');
    setIsModalOpen(true);
    setIsDropdownOpen(false);
  };

  const handleManageLocations = () => {
    setModalMode('manage');
    setIsModalOpen(true);
    setIsDropdownOpen(false);
  };

  if (!currentLocation) {
    return (
      <div style={{ 
        padding: '8px 12px', 
        background: 'rgba(255, 0, 0, 0.1)', 
        borderRadius: '4px',
        color: 'red',
        fontSize: '12px'
      }}>
        No Location
      </div>
    );
  }

  return (
    <div className="location-selector">
      <button 
        className="location-button"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
      >
        <MapPin size={16} />
        <span className="location-name">{currentLocation.name}</span>
        <ChevronDown size={14} />
      </button>

      {isDropdownOpen && (
        <>
          <div 
            className="dropdown-overlay"
            onClick={() => setIsDropdownOpen(false)}
          />
          <div className="location-dropdown">
            <div className="dropdown-header">
              <span>Select Location</span>
            </div>
            
            <div className="dropdown-content">
              {locations.map((location) => (
                <button
                  key={location.id}
                  className={`location-item ${currentLocation.id === location.id ? 'active' : ''}`}
                  onClick={() => handleLocationSwitch(location)}
                >
                  <div className="location-info">
                    <div className="location-item-name">{location.name}</div>
                    <div className="location-item-address">{location.address}</div>
                  </div>
                  {currentLocation.id === location.id && (
                    <div className="location-active-indicator">✓</div>
                  )}
                </button>
              ))}
            </div>

            <div className="dropdown-actions">
              <button 
                className="dropdown-action-btn"
                onClick={handleAddLocation}
              >
                <Plus size={16} />
                Add Location
              </button>
              <button 
                className="dropdown-action-btn"
                onClick={handleManageLocations}
              >
                <Settings size={16} />
                Manage Locations
              </button>
            </div>
          </div>
        </>
      )}

      {isModalOpen && (
        <LocationModal
          mode={modalMode}
          onClose={() => setIsModalOpen(false)}
        />
      )}

      <style jsx>{`
        .location-selector {
          position: relative;
        }

        .location-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          background: rgba(59, 130, 246, 0.1);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: 6px;
          color: #374151;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-weight: 500;
        }

        .location-button:hover {
          background: rgba(59, 130, 246, 0.15);
          border-color: rgba(59, 130, 246, 0.3);
        }

        .location-name {
          max-width: 150px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .dropdown-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 999;
        }

        .location-dropdown {
          position: absolute;
          top: 100%;
          right: 0;
          margin-top: 4px;
          background: white;
          border: 1px solid #e1e5e9;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          min-width: 280px;
          z-index: 1000;
        }

        .dropdown-header {
          padding: 12px 16px;
          border-bottom: 1px solid #e1e5e9;
          font-weight: 600;
          font-size: 14px;
          color: #202223;
        }

        .dropdown-content {
          max-height: 300px;
          overflow-y: auto;
        }

        .location-item {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          border: none;
          background: none;
          text-align: left;
          cursor: pointer;
          transition: background-color 0.2s ease;
        }

        .location-item:hover {
          background-color: #f6f6f7;
        }

        .location-item.active {
          background-color: #e3f2fd;
        }

        .location-info {
          flex: 1;
        }

        .location-item-name {
          font-weight: 500;
          color: #202223;
          margin-bottom: 2px;
        }

        .location-item-address {
          font-size: 12px;
          color: #6d7175;
        }

        .location-active-indicator {
          color: #3b82f6;
          font-weight: bold;
        }

        .dropdown-actions {
          border-top: 1px solid #e1e5e9;
          padding: 8px;
        }

        .dropdown-action-btn {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border: none;
          background: none;
          color: #202223;
          font-size: 14px;
          cursor: pointer;
          border-radius: 4px;
          transition: background-color 0.2s ease;
        }

        .dropdown-action-btn:hover {
          background-color: #f6f6f7;
        }

        .dropdown-action-btn + .dropdown-action-btn {
          margin-top: 4px;
        }
      `}</style>
    </div>
  );
};

export default LocationSelector;